import {
  createContext,
  useContext,
  ReactNode,
  Dispatch,
  SetStateAction,
  useState,
  useEffect,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ICandidate,
  getCandidateProfileDetails,
  ICandidateExperience,
  ICandidateEducation,
  ICandidateCertificationsData,
  ITimePeriod,
} from 'wisestep-api';

interface CandidateContextProps {
  candidateViewData: ICandidate | null;
  setCandidateViewData: Dispatch<SetStateAction<ICandidate | null>>;
  updateCandidateData: () => void;
}
const CandidateContext = createContext<CandidateContextProps | undefined>(undefined);
interface CandidateProviderProps {
  children: ReactNode;
}

const getDate = (period: ITimePeriod | null): Date | null =>
  period && period.year && period.month
    ? new Date(period.year, period.month - 1, period.day)
    : null;
const getUpdateExperiencedData = (current: ICandidateExperience): ICandidateExperience => {
  return {
    ...current,
    startDate: getDate(current.periodStart),
    endDate: getDate(current.periodEnd),
  };
};

const getUpdateEducationData = (current: ICandidateEducation): ICandidateEducation => {
  return {
    ...current,
    start: getDate(current.startDate),
    end: getDate(current.endDate),
  };
};

const getUpdateCertificationData = (
  current: ICandidateCertificationsData
): ICandidateCertificationsData => {
  return {
    ...current,
    start: getDate(current.startDate),
    end: getDate(current.endDate),
  };
};

const updateCandidateHistory = (response: ICandidate): ICandidate => {
  if (response?.experience?.current) {
    response.experience.current = getUpdateExperiencedData(response.experience.current);
  }
  if (response?.experience?.history) {
    response.experience.history = response?.experience?.history.map((exp) =>
      getUpdateExperiencedData(exp)
    );
  }
  if (response?.education) {
    response.education = response?.education?.map((edu) => getUpdateEducationData(edu));
  }
  if (response?.candidateCertificationsData) {
    response.candidateCertificationsData = response?.candidateCertificationsData?.map((cer) =>
      getUpdateCertificationData(cer)
    );
  }
  return { ...response };
};

export const CandidateViewDataProvider = ({ children }: CandidateProviderProps) => {
  const [candidateViewData, setCandidateViewData] = useState<ICandidate | null>(null);
  const [params] = useSearchParams();

  const updateCandidateData = async () => {
    if (params.get('id')) {
      setCandidateViewData(null);
      const response = await getCandidateProfileDetails(params.get('id') ?? '');
      setCandidateViewData(
        updateCandidateHistory({
          ...response.data,
        })
      );
    }
  };

  useEffect(() => {
    if (params.get('id')) {
      (async () => {
        try {
          setCandidateViewData(null);
          const response = await getCandidateProfileDetails(params.get('id') ?? '');
          setCandidateViewData(
            updateCandidateHistory({
              ...response.data,
            })
          );
        } catch (e) {
          console.log('Erro fetching candidate data');
        }
      })();
    }
  }, [params]);

  return (
    <CandidateContext.Provider
      value={{
        candidateViewData,
        setCandidateViewData,
        updateCandidateData,
      }}
    >
      {children}
    </CandidateContext.Provider>
  );
};
export const useCandidateViewData = (): CandidateContextProps => {
  const context = useContext(CandidateContext);
  if (!context) {
    throw new Error('useCandidate must be used within a CandidateProvider');
  }
  return context;
};
